import { MenuSearchResultBlockVariant, } from '@as-react/common-types';
import { SEARCH_RESULT_LIMIT } from '../constants/SearchConstants';
import { hasItems } from './ArrayUtil';
import { getProductDetailUrl } from './ProductUtil';
import { concatUrls } from './UrlUtil';
const URL_PARAMETER_SEARCH = 'q';
export const appendSearchQueryToUrl = (urlBase, searchTerm) => {
    const [path, existingQuery] = urlBase.split('?');
    const params = new URLSearchParams(existingQuery);
    params.set(URL_PARAMETER_SEARCH, searchTerm.trim());
    return `${path}?${params.toString()}`;
};
export const getKeywordsSuggestionsUrl = (root, searchTerm) => {
    return appendSearchQueryToUrl(`${root}lister.html`, searchTerm);
};
export const getBrandSuggestionsUrl = (brandPagePathPrefix, suggestion) => {
    return concatUrls(brandPagePathPrefix, suggestion.url);
};
export const getCategorySuggestionsUrl = (categoryPagePathPrefix, suggestion) => {
    return concatUrls(categoryPagePathPrefix, suggestion.url);
};
export const getBrandAsCategorySuggestionsUrl = (categoryPagePathPrefix, suggestion) => {
    return concatUrls(categoryPagePathPrefix, suggestion.url);
};
export const getSearchKeywords = (keywords = [], suggestionsParentLink) => suggestionsParentLink
    ? keywords.map(item => ({
        name: item.searchTerm,
        url: getKeywordsSuggestionsUrl(suggestionsParentLink, item.searchTerm),
    }))
    : [];
export const getSearchBrands = (brands = [], brandsParentLink) => brandsParentLink
    ? brands
        .filter(suggestion => Boolean(suggestion.url))
        .map(item => ({
        name: item.name,
        url: getBrandSuggestionsUrl(brandsParentLink, item),
    }))
    : [];
export const getSearchCategories = (categories = [], categoriesParentLink) => categoriesParentLink
    ? categories
        .filter(suggestion => Boolean(suggestion.url))
        .map(item => ({
        name: item.name,
        url: getCategorySuggestionsUrl(categoriesParentLink, item),
        subtitle: item.categoryNames.slice(0, item.categoryNames.length - 1),
    }))
    : [];
export const getSearchBrandAsCategories = (brandAsCategories = [], categoriesParentLink) => categoriesParentLink
    ? brandAsCategories
        .filter(suggestion => Boolean(suggestion.url))
        .map(item => ({
        name: item.brand,
        url: getBrandAsCategorySuggestionsUrl(categoriesParentLink, item),
        subtitle: item.categoryNames,
    }))
    : [];
export const getSearchContent = (content = []) => content
    .filter(suggestion => Boolean(suggestion.url))
    .map(item => ({
    name: item.title,
    url: item.url,
}));
export const getSearchProducts = (products = [], root) => root
    ? products
        .filter(product => Boolean(product.url))
        .map(product => {
        var _a, _b;
        return ({
            activeColourId: product.colourId,
            productId: product.productId,
            productCode: product.productCode,
            image: (_a = product.image) !== null && _a !== void 0 ? _a : '',
            alt: [product.brand, product.name].filter(Boolean).join(' '),
            currency: '',
            brand: product.brand,
            title: (_b = product.name) !== null && _b !== void 0 ? _b : '',
            url: getProductDetailUrl({ seoUrl: product.url, colourId: product.colourId }, root),
        });
    })
    : [];
export const wrapSubtitleWithTranslation = (menuSearchItem, limitSubtitles, t) => {
    let translatedSubtitle = undefined;
    if (menuSearchItem.subtitle && hasItems(menuSearchItem.subtitle)) {
        translatedSubtitle = t('flyout.search.result.category.subtitle', {
            category: concatSearchItemsSubtitles(menuSearchItem.subtitle, limitSubtitles),
        });
    }
    return Object.assign(Object.assign({}, menuSearchItem), { subtitle: [translatedSubtitle] });
};
export const concatSearchItemsSubtitles = (subtitles, limitSubtitles) => {
    let commaSeparatedSubtitles = undefined;
    if (!!subtitles && subtitles.length > 0) {
        commaSeparatedSubtitles = limitSubtitles ? subtitles.slice(0, 2).join(', ') : subtitles.join(', ');
    }
    return commaSeparatedSubtitles;
};
export const hasSearches = (categories, brands) => hasItems(categories) || hasItems(brands);
export const getBalancedBrandsAndCategoriesLimits = (brandsLength, categoriesLength, total) => {
    const half = Math.floor(total / 2);
    const brandsLimit = Math.min(brandsLength, Math.max(half, total - categoriesLength));
    const categoriesLimit = Math.min(categoriesLength, total - brandsLimit);
    return { brandsLimit, categoriesLimit };
};
export const getSearchResultLimit = (searchTerm) => {
    if (searchTerm) {
        switch (searchTerm.trim().length) {
            case 1:
                return SEARCH_RESULT_LIMIT.FOR_1_SEARCH_CHARACTER;
            case 2:
                return SEARCH_RESULT_LIMIT.FOR_2_SEARCH_CHARACTER;
            default:
                return SEARCH_RESULT_LIMIT.FOR_MORE_THEN_2_SEARCH_CHARACTER;
        }
    }
    else {
        return SEARCH_RESULT_LIMIT.FOR_1_SEARCH_CHARACTER;
    }
};
export const getSearchItemGrouping = ({ keywords = [], categories = [], brands = [], brandAsCategories = [], content = [], }, maxAmountEnhancedItems, t) => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (hasItems(keywords) && {
    [MenuSearchResultBlockVariant.SUGGESTIONS]: {
        type: MenuSearchResultBlockVariant.SUGGESTIONS,
        enableSuggestionFill: true,
        showTextSubtle: false,
        items: keywords.slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_SUGGESTIONS),
    },
})), (hasItems(categories) && {
    [MenuSearchResultBlockVariant.CATEGORIES]: {
        type: MenuSearchResultBlockVariant.CATEGORIES,
        enableSuggestionFill: false,
        showTextSubtle: false,
        items: categories.slice(0, maxAmountEnhancedItems).map(item => wrapSubtitleWithTranslation(item, true, t)),
    },
})), (hasItems(brands) && {
    [MenuSearchResultBlockVariant.BRANDS]: {
        type: MenuSearchResultBlockVariant.BRANDS,
        enableSuggestionFill: false,
        showTextSubtle: false,
        items: brands.slice(0, maxAmountEnhancedItems),
    },
})), (hasItems(brandAsCategories) && {
    [MenuSearchResultBlockVariant.BRAND_AS_CATEGORIES]: {
        type: MenuSearchResultBlockVariant.BRAND_AS_CATEGORIES,
        enableSuggestionFill: false,
        showTextSubtle: false,
        items: brandAsCategories
            .slice(0, maxAmountEnhancedItems)
            .map(item => wrapSubtitleWithTranslation(item, false, t)),
    },
})), (hasItems(content) && {
    [MenuSearchResultBlockVariant.CONTENT]: {
        type: MenuSearchResultBlockVariant.CONTENT,
        enableSuggestionFill: false,
        showTextSubtle: true,
        items: content.slice(0, maxAmountEnhancedItems),
    },
})));
export const getElasticSearchIndexWithEnv = (activeEnvironment, elasticsearchIndex) => elasticsearchIndex ? `${activeEnvironment}_${elasticsearchIndex}` : 'dev_www-asadventure-com_nl';
